import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { ArrowLeft, Store, Users, Coffee, Smartphone, Clock, Grid2X2 } from 'lucide-react';
import ChangePinModal from '../components/admin/ChangePinModal';

export default function EstablishmentDetails() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [establishment, setEstablishment] = useState(null);
    const [tables, setTables] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [waiters, setWaiters] = useState([]);
    const [waiterCalls, setWaiterCalls] = useState([]);
    const [waiterRolls, setWaiterRolls] = useState([]);

    const [activeTab, setActiveTab] = useState('overview');
    const [showPinModal, setShowPinModal] = useState(false);

    useEffect(() => {
        const fetchEstablishmentData = async () => {
            try {
                setLoading(true);

                const [
                    estRes,
                    tablesRes,
                    menuRes,
                    waitersRes,
                    callsRes,
                    rollsRes
                ] = await Promise.all([
                    supabase.from('establishments').select('*').eq('id', id).single(),
                    supabase.from('restaurant_tables').select('*').eq('establishment_id', id).order('table_number', { ascending: true }),
                    supabase.from('menu_items').select('*').eq('establishment_id', id).order('category', { ascending: true }),
                    supabase.from('waiters').select('*').eq('establishment_id', id),
                    supabase.from('waiter_calls').select('*').eq('establishment_id', id).order('created_at', { ascending: false }).limit(50),
                    supabase.from('waiter_rolls').select('*').eq('establishment_id', id).order('created_at', { ascending: false }).limit(20)
                ]);

                if (estRes.error) throw estRes.error;

                setEstablishment(estRes.data);
                setTables(tablesRes.data || []);
                setMenuItems(menuRes.data || []);
                setWaiters(waitersRes.data || []);
                setWaiterCalls(callsRes.data || []);
                setWaiterRolls(rollsRes.data || []);

            } catch (error) {
                console.error('Error fetching comprehensive details:', error);
                alert('Error fetching details for this establishment.');
            } finally {
                setLoading(false);
            }
        };

        fetchEstablishmentData();
    }, [id]);

    if (loading) {
        return <div style={{ padding: '40px', textAlign: 'center' }}>Loading Establishment Profile...</div>;
    }

    if (!establishment) {
        return <div style={{ padding: '40px', textAlign: 'center' }}>Establishment Not Found</div>;
    }

    const tabs = [
        { id: 'overview', label: 'Overview', icon: Store },
        { id: 'tables', label: `Tables (${tables.length})`, icon: Grid2X2 },
        { id: 'menu', label: `Menu (${menuItems.length})`, icon: Coffee },
        { id: 'staff', label: `Staff (${waiters.length})`, icon: Users },
        { id: 'activity', label: 'Recent Activity', icon: ActivityIcon },
    ];

    return (
        <div>
            <div style={{ marginBottom: '24px' }}>
                <button onClick={() => navigate('/admin/establishments')} className="btn btn-outline" style={{ padding: '6px 12px', marginBottom: '16px' }}>
                    <ArrowLeft size={16} /> Back to Directory
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: '64px', height: '64px', borderRadius: '12px', background: 'var(--bg-card)', border: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {establishment.logo_url ? (
                            <img src={establishment.logo_url} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }} />
                        ) : (
                            <Store color="var(--text-secondary)" size={32} />
                        )}
                    </div>
                    <div>
                        <h1 style={{ fontSize: '2rem', fontFamily: 'var(--font-display)', margin: 0 }}>{establishment.name}</h1>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                            Joined {new Date(establishment.created_at).toLocaleDateString()} &middot; {establishment.address || 'No Address provided'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom Tabs */}
            <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--border-light)', paddingBottom: '12px', marginBottom: '24px', overflowX: 'auto' }}>
                {tabs.map(tab => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                background: isActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                                color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.2s'
                            }}
                        >
                            <Icon size={16} /> {tab.label}
                        </button>
                    )
                })}
            </div>

            {/* Tab Content Areas */}

            {activeTab === 'overview' && (
                <div className="card">
                    <h3 style={{ marginBottom: '16px' }}>Establishment Information</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
                        <div>
                            <div className="form-label">Email Address</div>
                            <div style={{ fontWeight: 500 }}>{establishment.email || 'N/A'}</div>
                        </div>
                        <div>
                            <div className="form-label">Pin Code</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <code style={{ background: 'var(--bg-app)', padding: '4px 8px', borderRadius: '4px' }}>••••</code>
                                <button
                                    onClick={() => setShowPinModal(true)}
                                    className="btn btn-outline"
                                    style={{ padding: '4px 12px', fontSize: '0.75rem' }}
                                >
                                    Change PIN
                                </button>
                            </div>
                        </div>
                        <div>
                            <div className="form-label">Status</div>
                            <span className={`badge ${establishment.is_active ? 'badge-success' : 'badge-danger'}`}>
                                {establishment.is_active ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                        <div>
                            <div className="form-label">Coordinates</div>
                            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                Lat: {establishment.latitude || 'N/A'}<br />
                                Lng: {establishment.longitude || 'N/A'}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'tables' && (
                <div className="card" style={{ padding: 0 }}>
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Table Number</th>
                                    <th>Seat Capacity</th>
                                    <th>Current Status</th>
                                    <th>Table UID</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tables.map(t => (
                                    <tr key={t.id}>
                                        <td style={{ fontWeight: 600 }}>Table {t.table_number}</td>
                                        <td>{t.seats || 'N/A'}</td>
                                        <td>
                                            <span className={`badge ${t.status === 'Needs Attention' ? 'badge-danger' : 'badge-success'}`}>
                                                {t.status || 'Unknown'}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{t.id}</td>
                                    </tr>
                                ))}
                                {tables.length === 0 && <tr><td colSpan="4" style={{ textAlign: 'center', padding: '24px' }}>No tables registered.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'menu' && (
                <div className="card" style={{ padding: 0 }}>
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Item Name</th>
                                    <th>Category</th>
                                    <th>Price</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {menuItems.map(m => (
                                    <tr key={m.id}>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{m.name}</div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{m.description}</div>
                                        </td>
                                        <td><span className="badge" style={{ background: 'var(--bg-app)' }}>{m.category || 'Uncategorized'}</span></td>
                                        <td style={{ fontWeight: 600, color: 'var(--accent-success)' }}>${m.price?.toFixed(2)}</td>
                                        <td>{m.is_available ? 'Available' : 'Sold Out'}</td>
                                    </tr>
                                ))}
                                {menuItems.length === 0 && <tr><td colSpan="4" style={{ textAlign: 'center', padding: '24px' }}>No menu items found.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'staff' && (
                <div className="card" style={{ padding: 0 }}>
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Staff Name</th>
                                    <th>Login PIN</th>
                                    <th>Status</th>
                                    <th>Joined Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {waiters.map(w => (
                                    <tr key={w.id}>
                                        <td style={{ fontWeight: 600 }}>{w.name || 'Unnamed Staff Member'}</td>
                                        <td><code style={{ background: 'var(--bg-app)', padding: '4px 8px', borderRadius: '4px' }}>••••</code></td>
                                        <td>{w.is_verified ? 'Verified' : 'Pending'}</td>
                                        <td>{new Date(w.created_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                                {waiters.length === 0 && <tr><td colSpan="4" style={{ textAlign: 'center', padding: '24px' }}>No staff members registered.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'activity' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                    <div className="card" style={{ padding: 0 }}>
                        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Smartphone size={18} color="var(--accent-primary)" />
                            <h3 style={{ fontSize: '1rem', margin: 0 }}>Recent Customer Calls</h3>
                        </div>
                        <div className="table-container" style={{ border: 'none' }}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Table ID</th>
                                        <th>Status</th>
                                        <th>Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {waiterCalls.map(c => (
                                        <tr key={c.id}>
                                            <td style={{ fontSize: '0.875rem' }}>...{c.table_id.slice(-6)}</td>
                                            <td>
                                                <span className={`badge ${c.status === 'Resolved' ? 'badge-success' : 'badge-warning'}`}>
                                                    {c.status || 'Pending'}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                                {new Date(c.created_at).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                    {waiterCalls.length === 0 && <tr><td colSpan="3" style={{ textAlign: 'center', padding: '24px' }}>No call history.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="card" style={{ padding: 0 }}>
                        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Clock size={18} color="var(--accent-secondary)" />
                            <h3 style={{ fontSize: '1rem', margin: 0 }}>Recent Staff Shift Rolls</h3>
                        </div>
                        <div className="table-container" style={{ border: 'none' }}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Staff ID</th>
                                        <th>Type</th>
                                        <th>Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {waiterRolls.map(r => (
                                        <tr key={r.id}>
                                            <td style={{ fontSize: '0.875rem' }}>...{r.waiter_id.slice(-6)}</td>
                                            <td>
                                                <span className="badge" style={{ background: 'var(--bg-app)' }}>
                                                    {r.event || 'Roll Event'}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                                {new Date(r.timestamp).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                    {waiterRolls.length === 0 && <tr><td colSpan="3" style={{ textAlign: 'center', padding: '24px' }}>No staff shifts found.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {showPinModal && (
                <ChangePinModal
                    establishmentId={establishment.id}
                    establishmentName={establishment.name}
                    onClose={() => setShowPinModal(false)}
                    onSuccess={() => {
                        console.log('PIN changed successfully');
                        setShowPinModal(false);
                    }}
                />
            )}
        </div>
    );
}

// Custom Activity Icon component since we need it in the tabs array
const ActivityIcon = (props) => (
    <svg
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
);
